/**
 * usePaymentGateListener
 *
 * Real-time Firestore listener for workflow payment status updates.
 * Used by the payment-gate workflow to detect when a payment succeeds, fails, or is cancelled.
 *
 * The hook subscribes to: users/{uid}/workflow_payments/{paymentIntentId}
 * and calls the appropriate callback when the status changes.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import db from '../utils/firestore';

// ============================================================================
// Types
// ============================================================================

export type PaymentStatus = 'pending' | 'processing' | 'succeeded' | 'failed' | 'cancelled';

export interface WorkflowPaymentData {
  paymentIntentId: string;
  clientSecret: string;
  amountUsd: number;
  amountCents: number;
  status: PaymentStatus;
  workflowId: string;
  workflowStepId: string;
  targetUid: string;
  createdBy: string;
  allowedMethods: string[];
  description: string;
  invoiceTitle: string;
  paymentMethod?: string;
  paidAt?: Date;
  failureMessage?: string;
  metadata: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export interface PaymentGateListenerState {
  /** Current payment status */
  status: PaymentStatus;
  /** Whether the listener is loading initial data */
  isLoading: boolean;
  /** Error message if listener setup failed */
  error: string | null;
  /** Full payment data from Firestore */
  paymentData: WorkflowPaymentData | null;
  /** Whether the payment is in a terminal state (succeeded, failed, cancelled) */
  isTerminal: boolean;
}

export interface PaymentGateListenerCallbacks {
  /** Called when payment succeeds */
  onSuccess?: (data: WorkflowPaymentData) => void;
  /** Called when payment fails */
  onFailed?: (data: WorkflowPaymentData, errorMessage: string) => void;
  /** Called when payment is cancelled */
  onCancelled?: (data: WorkflowPaymentData) => void;
  /** Called on any status change */
  onStatusChange?: (status: PaymentStatus, data: WorkflowPaymentData | null) => void;
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Real-time listener for workflow payment status.
 *
 * @param uid - The user ID whose payment to watch
 * @param paymentIntentId - The Stripe PaymentIntent ID (also the Firestore doc ID)
 * @param callbacks - Optional callbacks for payment events
 * @returns Current payment state and manual refresh function
 *
 * @example
 * ```tsx
 * const { status, isLoading, paymentData } = usePaymentGateListener(
 *   currentUser.uid,
 *   paymentIntentId,
 *   {
 *     onSuccess: (data) => console.log('Payment succeeded!', data),
 *     onFailed: (data, error) => console.log('Payment failed:', error),
 *   }
 * );
 * ```
 */
export function usePaymentGateListener(
  uid: string | null | undefined,
  paymentIntentId: string | null | undefined,
  callbacks?: PaymentGateListenerCallbacks
): PaymentGateListenerState & { unsubscribe: () => void } {
  const [state, setState] = useState<PaymentGateListenerState>({
    status: 'pending',
    isLoading: true,
    error: null,
    paymentData: null,
    isTerminal: false,
  });

  // Store callbacks in ref to avoid re-subscribing when callbacks change
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  // Store previous status to detect changes
  const prevStatusRef = useRef<PaymentStatus | null>(null);

  // Unsubscribe ref for cleanup
  const unsubscribeRef = useRef<Unsubscribe | null>(null);

  // Manual unsubscribe function
  const unsubscribe = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Clean up any existing subscription
    unsubscribe();

    // Skip if missing required params
    if (!uid || !paymentIntentId) {
      setState({
        status: 'pending',
        isLoading: false,
        error: null,
        paymentData: null,
        isTerminal: false,
      });
      return;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    const docRef = doc(db, `users/${uid}/workflow_payments/${paymentIntentId}`);

    unsubscribeRef.current = onSnapshot(
      docRef,
      (snapshot) => {
        if (!snapshot.exists()) {
          setState({
            status: 'pending',
            isLoading: false,
            error: 'Payment record not found',
            paymentData: null,
            isTerminal: false,
          });
          return;
        }

        const data = snapshot.data();
        const status = (data.status as PaymentStatus) || 'pending';
        const isTerminal = status === 'succeeded' || status === 'failed' || status === 'cancelled';

        // Convert Firestore timestamps to Date objects
        const paymentData: WorkflowPaymentData = {
          paymentIntentId: data.paymentIntentId || paymentIntentId,
          clientSecret: data.clientSecret || '',
          amountUsd: data.amountUsd || 0,
          amountCents: data.amountCents || 0,
          status,
          workflowId: data.workflowId || '',
          workflowStepId: data.workflowStepId || '',
          targetUid: data.targetUid || uid,
          createdBy: data.createdBy || '',
          allowedMethods: data.allowedMethods || ['card', 'bank', 'ach'],
          description: data.description || '',
          invoiceTitle: data.invoiceTitle || '',
          paymentMethod: data.paymentMethod,
          paidAt: data.paidAt?.toDate?.() || (data.paidAt ? new Date(data.paidAt) : undefined),
          failureMessage: data.failureMessage,
          metadata: data.metadata || {},
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.() || new Date(),
        };

        setState({
          status,
          isLoading: false,
          error: null,
          paymentData,
          isTerminal,
        });

        // Trigger callbacks on status change
        const prevStatus = prevStatusRef.current;
        if (prevStatus !== status) {
          prevStatusRef.current = status;

          // Always call onStatusChange
          callbacksRef.current?.onStatusChange?.(status, paymentData);

          // Call specific callbacks
          if (status === 'succeeded') {
            callbacksRef.current?.onSuccess?.(paymentData);
          } else if (status === 'failed') {
            callbacksRef.current?.onFailed?.(paymentData, paymentData.failureMessage || 'Payment failed');
          } else if (status === 'cancelled') {
            callbacksRef.current?.onCancelled?.(paymentData);
          }
        }
      },
      (error) => {
        console.error('Payment listener error:', error);
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: error.message || 'Failed to listen for payment updates',
        }));
      }
    );

    // Cleanup on unmount or when params change
    return () => {
      unsubscribe();
    };
  }, [uid, paymentIntentId, unsubscribe]);

  return { ...state, unsubscribe };
}

export default usePaymentGateListener;
